import { useEffect, useRef, useState } from 'react'

import { readTabState, writeTabState } from './workspace-tab-state'
import { useWorkspaceTabsStore } from './workspace-tabs-store'

/**
 * `useState` whose value belongs to the active workspace tab: each tab sees
 * its own copy, and the value survives the unmount a workspace-tab switch
 * causes (in-memory only — a reload starts from `initial` again, like any
 * other component state). Use it for per-tab UI state that must come back
 * when the user switches to another workspace tab and returns — e.g. a page
 * dialog's open flag. `key` only needs to be unique within one page, since
 * a workspace tab shows a single page at a time.
 */
export function useWorkspaceTabState<T>(
  key: string,
  initial: T,
): [T, (value: T) => void] {
  const activeTabId = useWorkspaceTabsStore((state) => state.activeTabId)
  const [value, setValue] = useState<T>(() =>
    readTabState(activeTabId, key, initial),
  )

  // Swap to the new tab's copy when the workspace tab changes while this
  // component stays mounted (both tabs on the same route).
  const lastTab = useRef(activeTabId)
  useEffect(() => {
    if (lastTab.current === activeTabId) return
    lastTab.current = activeTabId
    setValue(readTabState(activeTabId, key, initial))
  }, [activeTabId, key, initial])

  const set = (next: T) => {
    setValue(next)
    writeTabState(activeTabId, key, next)
  }
  return [value, set]
}
